<template>
  <div class="product-detail-page">
    <NavBar />
    
    <div class="detail-container">
      <!-- 面包屑导航 -->
      <a-breadcrumb class="breadcrumb">
        <a-breadcrumb-item><a href="/">首页</a></a-breadcrumb-item>
        <a-breadcrumb-item><a href="/category">商品分类</a></a-breadcrumb-item>
        <a-breadcrumb-item>商品详情</a-breadcrumb-item>
      </a-breadcrumb>
      
      <!-- 商品信息区 -->
      <div class="product-main">
        <!-- 左侧：图片展示 -->
        <div class="product-gallery">
          <div class="main-image">
            <img :src="currentImage" alt="商品图片" />
          </div>
          <div class="image-list">
            <div 
              v-for="(img, index) in product.images_url" 
              :key="index"
              :class="['image-item', { active: currentImage === img }]"
              @click="currentImage = img"
            >
              <img :src="img" :alt="`图片${index + 1}`" />
            </div>
          </div>
        </div>
        
        <!-- 右侧：商品信息 -->
        <div class="product-info">
          <h1 class="product-title">{{ product.name }}</h1>
          
          <div class="price-section">
            <div class="current-price">
              <span class="price-label">¥</span>
              <span class="price-value">{{ product.price }}</span>
            </div>
            <div v-if="product.original_price && product.price < product.original_price" class="original-price">
              原价：¥{{ product.original_price }}
            </div>
          </div>
          
          <div class="product-meta">
            <div class="meta-item">
              <span class="meta-label">销量</span>
              <span class="meta-value">{{ product.sales }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">库存</span>
              <span class="meta-value">{{ product.stock }}</span>
            </div>
          </div>
          
          <div class="purchase-section">
            <div class="quantity-selector">
              <span class="selector-label">数量</span>
              <a-input-number 
                v-model="quantity" 
                :min="1" 
                :max="product.stock"
                class="quantity-input"
                :style="{ borderRadius: '24px' }"
              />
            </div>
            
            <div class="action-buttons">
              <a-button type="primary" size="large" class="buy-now-btn" @click="handleBuyNow">
                立即购买
              </a-button>
              <a-button size="large" class="add-cart-btn" @click="handleAddCart">
                加入购物车
              </a-button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 商品详情 -->
      <div class="product-detail">
        <div class="detail-tabs">
          <div class="tab-item active">商品详情</div>
        </div>
        <div class="detail-content" v-html="product.content"></div>
      </div>
    </div>
    
    <!-- 地址填写弹窗 -->
    <a-modal 
      v-model:visible="showAddressModal" 
      title="填写收货地址"
      width="500px"
      @ok="handleSubmitBuyNow"
      ok-text="确认下单"
      cancel-text="取消"
    >
      <a-form :model="addressForm" layout="vertical">
        <a-form-item label="收货人姓名" field="contact_name">
          <a-input v-model="addressForm.contact_name" placeholder="请输入收货人姓名" />
        </a-form-item>
        <a-form-item label="联系电话" field="contact_phone">
          <a-input v-model="addressForm.contact_phone" placeholder="请输入手机号" />
        </a-form-item>
        <a-form-item label="省份" field="province">
          <a-input v-model="addressForm.province" placeholder="请输入省份，如：北京市" />
        </a-form-item>
        <a-form-item label="详细地址" field="address">
          <a-textarea 
            v-model="addressForm.address" 
            placeholder="请输入详细地址"
            :auto-size="{ minRows: 2, maxRows: 4 }"
          />
        </a-form-item>
        <a-form-item label="订单备注（选填）" field="remark">
          <a-textarea 
            v-model="addressForm.remark" 
            placeholder="请输入订单备注"
            :auto-size="{ minRows: 2, maxRows: 4 }"
          />
        </a-form-item>
      </a-form>
    </a-modal>
    
    <!-- 支付二维码弹窗 -->
    <a-modal 
      v-model:visible="showPaymentModal" 
      title="扫码支付"
      width="400px"
      :footer="false"
      @cancel="showPaymentModal = false"
    >
      <div class="payment-content">
        <div class="order-info">
          <p class="order-no">订单号：{{ orderInfo?.order_no }}</p>
          <p class="order-amount">支付金额：<span class="amount">¥{{ (product.price * quantity).toFixed(2) }}</span></p>
        </div>
        <div class="qrcode-container">
          <img src="@/assets/zhifu.png" alt="支付二维码" class="qrcode" />
        </div>
        <p class="payment-tip">请使用微信/支付宝扫码支付</p>
      </div>
    </a-modal>
    
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'
import { getProductDetail, addToCart, buyNow } from '@/api/index.js'

export default {
  name: 'ProductDetail',
  components: {
    NavBar,
    Footer
  },
  data() {
    return {
      product: {
        images_url: []
      },
      currentImage: '',
      quantity: 1,
      showAddressModal: false,
      showPaymentModal: false,
      addressForm: {
        contact_name: '',
        contact_phone: '',
        province: '',
        address: '',
        remark: ''
      },
      orderInfo: null
    }
  },
  mounted() {
    this.loadProductDetail()
  },
  methods: {
    async loadProductDetail() {
      const id = this.$route.params.id
      if (!id) {
        this.$message.error('商品ID不存在')
        this.$router.push('/')
        return
      }
      
      try {
        const result = await getProductDetail(id)
        if (result.code === 1 && result.data) {
          this.product = result.data
          // 设置默认显示第一张图片
          if (this.product.images_url && this.product.images_url.length > 0) {
            this.currentImage = this.product.images_url[0]
          } else if (this.product.cover_url) {
            this.currentImage = this.product.cover_url
            this.product.images_url = [this.product.cover_url]
          }
        } else {
          this.$message.error(result.msg || '商品不存在')
          this.$router.push('/')
        }
      } catch (error) {
        console.error('加载商品详情失败:', error)
        this.$message.error('加载商品详情失败')
      }
    },
    handleBuyNow() {
      // 检查登录状态
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }
      
      // 显示地址填写弹窗
      this.showAddressModal = true
    },
    async handleSubmitBuyNow() {
      // 表单验证
      if (!this.addressForm.contact_name) {
        this.$message.warning('请输入收货人姓名')
        return
      }
      if (!this.addressForm.contact_phone) {
        this.$message.warning('请输入联系电话')
        return
      }
      const phoneReg = /^1[3-9]\d{9}$/
      if (!phoneReg.test(this.addressForm.contact_phone)) {
        this.$message.warning('请输入正确的手机号格式')
        return
      }
      if (!this.addressForm.province) {
        this.$message.warning('请输入省份')
        return
      }
      if (!this.addressForm.address) {
        this.$message.warning('请输入详细地址')
        return
      }
      
      const token = localStorage.getItem('token')
      try {
        const result = await buyNow({
          shopitem_id: this.product.id,
          quantity: this.quantity,
          ...this.addressForm
        }, token)
        
        if (result.code === 1) {
          this.$message.success('下单成功')
          this.orderInfo = result.data
          this.showAddressModal = false
          
          // 显示支付二维码弹窗
          this.showPaymentModal = true
        } else {
          this.$message.error(result.msg || '下单失败')
        }
      } catch (error) {
        console.error('立即购买失败:', error)
        this.$message.error('下单失败，请稍后重试')
      }
    },
    async handleAddCart() {
      // 检查登录状态
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }
      
      try {
        const result = await addToCart({
          shopitem_id: this.product.id,
          quantity: this.quantity
        }, token)
        
        if (result.code === 1) {
          this.$message.success('已加入购物车')
          // 触发购物车更新事件
          window.dispatchEvent(new Event('cart-updated'))
        } else {
          this.$message.error(result.msg || '加入购物车失败')
        }
      } catch (error) {
        console.error('加入购物车失败:', error)
        if (error.message && error.message.includes('请先登录')) {
          this.$message.warning('请先登录')
          this.$router.push('/login')
        } else {
          this.$message.error('加入购物车失败，请稍后重试')
        }
      }
    }
  }
}
</script>

<style scoped>
.product-detail-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.detail-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 40px 60px;
}

.breadcrumb {
  margin-bottom: 24px;
}

/* 商品主要信息区 */
.product-main {
  display: flex;
  gap: 40px;
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* 左侧图片展示 */
.product-gallery {
  flex: 0 0 500px;
}

.main-image {
  width: 100%;
  height: 500px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e6eb;
  margin-bottom: 16px;
}

.main-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-list {
  display: flex;
  gap: 12px;
  overflow-x: auto;
}

.image-item {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e5e6eb;
  cursor: pointer;
  transition: all 0.3s;
  flex-shrink: 0;
}

.image-item:hover {
  border-color: #ff69b4;
}

.image-item.active {
  border-color: #ff1493;
  box-shadow: 0 0 0 2px rgba(255, 20, 147, 0.2);
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 右侧商品信息 */
.product-info {
  flex: 1;
}

.product-title {
  font-size: 24px;
  font-weight: 700;
  color: #1d2129;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.price-section {
  background: linear-gradient(135deg, #fff5f9, #ffeef8);
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
}

.current-price {
  display: flex;
  align-items: baseline;
  margin-bottom: 8px;
}

.price-label {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(90deg, #ff69b4, #ff1493);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.price-value {
  font-size: 36px;
  font-weight: 700;
  background: linear-gradient(90deg, #ff69b4, #ff1493);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.original-price {
  font-size: 14px;
  color: #86909c;
  text-decoration: line-through;
}

.product-meta {
  display: flex;
  gap: 40px;
  padding: 20px 0;
  border-bottom: 1px solid #e5e6eb;
  margin-bottom: 24px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meta-label {
  font-size: 14px;
  color: #86909c;
}

.meta-value {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}

.purchase-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.quantity-selector {
  display: flex;
  align-items: center;
  gap: 16px;
}

.selector-label {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}

.quantity-input {
  width: 150px;
  border-radius: 24px !important;
}

.quantity-input >>> .arco-input-number,
.quantity-input ::v-deep .arco-input-number {
  border-radius: 24px !important;
}

.quantity-input >>> .arco-input-number-step,
.quantity-input ::v-deep .arco-input-number-step {
  border-radius: 0 24px 24px 0 !important;
}

.quantity-input >>> .arco-input-number-step-button:first-child,
.quantity-input ::v-deep .arco-input-number-step-button:first-child {
  border-top-right-radius: 24px !important;
}

.quantity-input >>> .arco-input-number-step-button:last-child,
.quantity-input ::v-deep .arco-input-number-step-button:last-child {
  border-bottom-right-radius: 24px !important;
}

.action-buttons {
  display: flex;
  gap: 16px;
}

.buy-now-btn,
.add-cart-btn {
  flex: 1;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 25px;
}

.buy-now-btn {
  background: linear-gradient(90deg, #ff69b4, #ff1493) !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(255, 105, 180, 0.3);
}

.buy-now-btn:hover {
  background: linear-gradient(90deg, #ff1493, #ff69b4) !important;
  box-shadow: 0 6px 16px rgba(255, 105, 180, 0.4);
}

.add-cart-btn {
  border: 2px solid #ff1493 !important;
  color: #ff1493 !important;
  background: #fff !important;
}

.add-cart-btn:hover {
  background: rgba(255, 105, 180, 0.05) !important;
}

/* 商品详情 */
.product-detail {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.detail-tabs {
  display: flex;
  border-bottom: 1px solid #e5e6eb;
}

.tab-item {
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
}

.tab-item.active {
  color: #ff1493;
  border-bottom: 3px solid #ff1493;
}

.detail-content {
  padding: 40px;
  line-height: 1.8;
  color: #1d2129;
}

.detail-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

/* 响应式 */
@media (max-width: 1024px) {
  .product-main {
    flex-direction: column;
  }
  
  .product-gallery {
    flex: none;
    width: 100%;
  }
}

@media (max-width: 768px) {
  .detail-container {
    padding: 20px;
  }
  
  .product-main {
    padding: 20px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
}

/* 地址弹窗样式 */
:deep(.arco-modal-footer .arco-btn-primary) {
  background: linear-gradient(90deg, #ff69b4, #ff1493) !important;
  border: none !important;
}

:deep(.arco-modal-footer .arco-btn-primary:hover) {
  background: linear-gradient(90deg, #ff1493, #ff69b4) !important;
}

/* 支付二维码弹窗样式 */
.payment-content {
  text-align: center;
  padding: 20px 0;
}

.order-info {
  margin-bottom: 24px;
}

.order-no {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.order-amount {
  font-size: 16px;
  color: #1d2129;
}

.amount {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(90deg, #ff69b4, #ff1493);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-left: 8px;
}

.qrcode-container {
  display: flex;
  justify-content: center;
  margin: 24px 0;
}

.qrcode {
  width: 200px;
  height: 200px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
}

.payment-tip {
  font-size: 14px;
  color: #86909c;
  margin-bottom: 24px;
}
</style>
